import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import Editor from "@monaco-editor/react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DoneIcon from "@mui/icons-material/Done";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WrapTextIcon from "@mui/icons-material/WrapText";

import {
  EDITOR_THEME_LIGHT_NAME,
  defineLightEditorTheme,
} from "../../../theme/codeTheme";
import {
  isRelativeAssetHref,
  renderMarkdown,
} from "../../../utils/markdownRenderer";
import {
  MARKDOWN_MONO_FONT,
  MARKDOWN_PROSE_SX,
} from "../../../utils/markdownProseSx";
import {
  ACCENT,
  ACCENT_TINT,
  FONT,
  HAIRLINE,
  INK,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  SURFACE_MUTED,
} from "../../../theme/tokens";

const MONO_FONT = MARKDOWN_MONO_FONT;

export function formatReaderBytes(bytes) {
  if (typeof bytes !== "number" || Number.isNaN(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const EDITOR_OPTIONS_BASE = {
  readOnly: true,
  domReadOnly: true,
  minimap: { enabled: false },
  lineNumbers: "on",
  scrollBeyondLastLine: false,
  fontSize: 13,
  lineHeight: 20,
  tabSize: 2,
  renderLineHighlight: "line",
  overviewRulerLanes: 0,
  overviewRulerBorder: false,
  contextmenu: false,
  smoothScrolling: true,
  fontFamily: MONO_FONT,
  padding: { top: 10, bottom: 10 },
  scrollbar: { alwaysConsumeMouseWheel: false },
};

function CopyButton({ value, title = "Copy", label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  const handleCopy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Labeled (text) variant
  if (label) {
    return (
      <Button
        size="small"
        variant="outlined"
        onClick={handleCopy}
        startIcon={
          copied ? (
            <DoneIcon sx={{ fontSize: 15 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 15 }} />
          )
        }
        sx={{
          flexShrink: 0,
          textTransform: "none",
          fontSize: FONT.xs,
          fontWeight: 600,
          color: copied ? ACCENT : INK_SECONDARY,
          borderColor: HAIRLINE,
          whiteSpace: "nowrap",
          "&:hover": { borderColor: ACCENT, color: ACCENT },
        }}
      >
        {copied ? "Copied" : label}
      </Button>
    );
  }

  // Icon-only variant
  return (
    <Tooltip title={copied ? "Copied" : title}>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{ color: copied ? ACCENT : INK_MUTED }}
      >
        {copied ? (
          <DoneIcon sx={{ fontSize: 17 }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}

CopyButton.propTypes = {
  value: PropTypes.string,
  title: PropTypes.string,
  label: PropTypes.string,
};

export default function FilePreview({
  fileName,
  filePath,
  language,
  languageLabel,
  content,
  sizeText,
  lineCount,
  lastCommit,
  loading,
  error,
  previewBlocked,
  warning,
  canForceOpen,
  onForceOpen,
  webUrl,
  resolveAssetUrl,
}) {
  const isMarkdown = language === "markdown";
  const [mdView, setMdView] = useState("rendered"); // 'rendered' | 'raw'
  const [wrap, setWrap] = useState(false);
  const markdownContainerRef = useRef(null);

  const showRenderedMarkdown = isMarkdown && mdView === "rendered";

  // Rewrite markdown-relative <img> src values (e.g. "assets/fig.png") to
  // wherever resolveAssetUrl says they actually live — the browser would
  // otherwise resolve them against the app's own URL and 404.
  useEffect(() => {
    if (!showRenderedMarkdown || !resolveAssetUrl) return undefined;
    const container = markdownContainerRef.current;
    if (!container) return undefined;

    const relativeImages = Array.from(
      container.querySelectorAll("img[src]"),
    ).filter((img) => isRelativeAssetHref(img.getAttribute("src")));
    if (!relativeImages.length) return undefined;

    let cancelled = false;
    const createdBlobUrls = [];

    relativeImages.forEach((img) => {
      const relativeSrc = img.getAttribute("src");
      resolveAssetUrl(relativeSrc)
        .then((resolvedUrl) => {
          if (cancelled || !resolvedUrl) return;
          img.src = resolvedUrl;
          if (resolvedUrl.startsWith("blob:"))
            createdBlobUrls.push(resolvedUrl);
        })
        .catch((err) => {
          console.error(
            `[FilePreview] Failed to resolve image "${relativeSrc}":`,
            err,
          );
        });
    });

    return () => {
      cancelled = true;
      createdBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [showRenderedMarkdown, resolveAssetUrl, content]);

  const metaParts = [languageLabel];
  if (typeof lineCount === "number" && lineCount > 0) {
    metaParts.push(`${lineCount} line${lineCount !== 1 ? "s" : ""}`);
  }
  if (sizeText) metaParts.push(sizeText);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 0,
        height: "100%", // fill the SideDrawer so the editor can grow to fit
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: `${RADIUS.sm}px`,
            display: "grid",
            placeItems: "center",
            backgroundColor: ACCENT_TINT,
            color: ACCENT,
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: FONT.lg,
              fontWeight: 600,
              color: INK,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={fileName}
          >
            {fileName || "Untitled"}
          </Typography>
          {!loading && (
            <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED }}>
              {metaParts.join(" · ")}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {!loading && !previewBlocked && isMarkdown && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mdView}
              onChange={(_e, v) => v && setMdView(v)}
              sx={{
                mr: 0.5,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontSize: FONT.xs,
                  fontWeight: 600,
                  px: 1.25,
                  py: 0.25,
                  color: INK_SECONDARY,
                  borderColor: HAIRLINE,
                  "&.Mui-selected": {
                    color: ACCENT,
                    backgroundColor: ACCENT_TINT,
                    "&:hover": { backgroundColor: ACCENT_TINT },
                  },
                },
              }}
            >
              <ToggleButton value="rendered">Rendered</ToggleButton>
              <ToggleButton value="raw">Raw</ToggleButton>
            </ToggleButtonGroup>
          )}

          {!loading && !previewBlocked && !showRenderedMarkdown && (
            <Tooltip title={wrap ? "Disable soft wrap" : "Soft wrap"}>
              <IconButton
                size="small"
                onClick={() => setWrap((w) => !w)}
                sx={{ color: wrap ? ACCENT : INK_MUTED }}
              >
                <WrapTextIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          {!loading && !previewBlocked && (
            <CopyButton value={content} title="Copy file" />
          )}

          {webUrl && (
            <Tooltip title="Open in GitLab">
              <IconButton
                size="small"
                component="a"
                href={webUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: INK_MUTED }}
              >
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Path + copy */}
      {filePath && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: SURFACE_MUTED,
            border: `1px solid ${HAIRLINE}`,
            borderRadius: `${RADIUS.sm}px`,
            pl: 1.25,
            pr: 0.5,
            py: 0.375,
          }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontFamily: MONO_FONT,
              fontSize: FONT.xs,
              color: INK_SECONDARY,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={filePath}
          >
            {filePath}
          </Typography>
          <CopyButton value={filePath} label="Copy path" />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {lastCommit && !loading && (
        <Typography
          sx={{
            fontSize: FONT.xs,
            color: INK_SECONDARY,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <Box component="span" sx={{ color: INK_MUTED }}>
            Last commit:{" "}
          </Box>
          {lastCommit}
        </Typography>
      )}

      {/* Body */}
      {loading ? (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <CircularProgress size={22} sx={{ color: ACCENT }} />
          <Typography sx={{ color: INK_SECONDARY }}>
            Loading file preview…
          </Typography>
        </Box>
      ) : previewBlocked ? (
        <Alert
          severity="warning"
          sx={{
            alignItems: "center",
            "& .MuiAlert-action": {
              alignItems: "center",
              pt: 0,
            },
          }}
          action={
            canForceOpen && onForceOpen ? (
              <Button color="inherit" size="small" onClick={onForceOpen}>
                Open anyway
              </Button>
            ) : null
          }
        >
          {warning || "Preview is not available for this file."}
        </Alert>
      ) : showRenderedMarkdown ? (
        <Box
          ref={markdownContainerRef}
          sx={[
            {
              border: `1px solid ${HAIRLINE}`,
              borderRadius: `${RADIUS.md}px`,
              backgroundColor: SURFACE,
              p: { xs: 2, sm: 3 },
              maxHeight: "calc(100vh - 240px)",
              overflow: "auto",
            },
            MARKDOWN_PROSE_SX,
          ]}
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content || "") }}
        />
      ) : content ? (
        <Box
          sx={{
            flex: 1, // grow to fill the remaining drawer height
            minHeight: 240, // ...but stay usable on short viewports
            border: `1px solid ${HAIRLINE}`,
            borderRadius: `${RADIUS.md}px`,
            overflow: "hidden",
          }}
        >
          <Editor
            height="100%"
            language={language}
            theme={EDITOR_THEME_LIGHT_NAME}
            beforeMount={defineLightEditorTheme}
            value={content}
            options={{ ...EDITOR_OPTIONS_BASE, wordWrap: wrap ? "on" : "off" }}
            loading={
              <Box
                sx={{ display: "grid", placeItems: "center", height: "100%" }}
              >
                <CircularProgress size={20} sx={{ color: ACCENT }} />
              </Box>
            }
          />
        </Box>
      ) : (
        <Box
          sx={{
            border: `1px solid ${HAIRLINE}`,
            borderRadius: `${RADIUS.md}px`,
            backgroundColor: SURFACE,
            px: 2,
            py: 3,
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
            Empty file
          </Typography>
        </Box>
      )}
    </Box>
  );
}

FilePreview.propTypes = {
  fileName: PropTypes.string,
  filePath: PropTypes.string,
  language: PropTypes.string,
  languageLabel: PropTypes.string,
  content: PropTypes.string,
  sizeText: PropTypes.string,
  lineCount: PropTypes.number,
  lastCommit: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  previewBlocked: PropTypes.bool,
  warning: PropTypes.string,
  canForceOpen: PropTypes.bool,
  onForceOpen: PropTypes.func,
  webUrl: PropTypes.string,
  resolveAssetUrl: PropTypes.func,
};

FilePreview.defaultProps = {
  fileName: "",
  filePath: "",
  language: "plaintext",
  languageLabel: "Text",
  content: "",
  sizeText: "",
  lineCount: 0,
  lastCommit: "",
  loading: false,
  error: null,
  previewBlocked: false,
  warning: null,
  canForceOpen: false,
  webUrl: null,
};
